import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Eye, EyeOff, Save, X, ShieldOff, Award, BarChart3 } from "lucide-react";
import { api } from "../../lib/api";

const PLANS = ["", "Reset", "Momentum", "Elevate"];
const BLANK = {
  name: "", public_name: "", industry: "", bbb_before: "", bbb_after: "",
  months: 0, savings_monthly: 0, plan: "", quote: "", levers: [],
  anonymize: false, published: false, sort_order: 0,
};

const inputCls = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";
const labelCls = "text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50";

export default function CaseStudiesAdmin() {
  const [cases, setCases] = useState(null);
  const [editing, setEditing] = useState(null); // draft object (new or existing)
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/case-studies");
      setCases(data.cases || []);
    } catch {
      setCases([]);
      toast.error("Could not load case studies.");
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...BLANK, sort_order: (cases?.length || 0) });
  const startEdit = (c) => setEditing({ ...BLANK, ...c, levers: c.levers || [] });

  const save = async () => {
    const d = editing;
    if (!d.name.trim() && !d.public_name.trim()) return toast.error("Add a client name (or a public alias).");
    setSaving(true);
    const payload = {
      ...d,
      months: Number(d.months) || 0,
      savings_monthly: Number(d.savings_monthly) || 0,
      sort_order: Number(d.sort_order) || 0,
      levers: (Array.isArray(d.levers) ? d.levers : String(d.levers || "").split(","))
        .map((x) => String(x).trim()).filter(Boolean),
    };
    try {
      if (d.id) await api.put(`/admin/case-studies/${d.id}`, payload);
      else await api.post("/admin/case-studies", payload);
      toast.success(d.id ? "Case study updated." : "Case study added.");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (c) => {
    const next = !c.published;
    try {
      await api.put(`/admin/case-studies/${c.id}`, { ...c, published: next });
      await load();  // refetch so the row + public site stay in sync (no optimistic race)
      toast.success(next ? "Now live on the website." : "Hidden from the website.");
    } catch { toast.error("Could not update."); }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/admin/case-studies/${delTarget.id}`);
      setCases((list) => list.filter((x) => x.id !== delTarget.id));
      toast.success("Case study removed.");
      setDelTarget(null);
    } catch { toast.error("Could not delete."); }
  };

  if (cases === null) {
    return <div className="py-14 flex items-center justify-center gap-3 text-[#1e3d3a]/55"><Loader2 className="h-4 w-4 animate-spin" /> Loading case studies…</div>;
  }

  return (
    <div data-testid="tab-content-case-studies" className="space-y-5">
      <HeadlineStatsEditor />

      <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
        <div>
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Recovery success wall</span>
          <p className="mt-1 text-sm text-[#1e3d3a]/70 max-w-xl">
            These stories appear on your public site under “Real climbs. Real numbers.” Only
            <span className="font-medium text-[#1e3d3a]"> published</span> stories are shown. Turn on
            <span className="font-medium text-[#1e3d3a]"> anonymize</span> to hide a client’s real name and show a public alias.
          </p>
        </div>
        <button
          type="button"
          data-testid="case-add-btn"
          onClick={startNew}
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
        >
          <Plus className="h-3.5 w-3.5" /> Add story
        </button>
      </div>

      {cases.length === 0 && (
        <div className="py-12 text-center text-[#1e3d3a]/45 text-sm rounded-2xl border border-[#1e3d3a]/12 bg-white">
          No case studies yet. Add your first real client success story.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {cases.map((c) => (
          <div key={c.id} data-testid={`case-row-${c.id}`} className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-head text-lg text-[#1e3d3a] truncate">{c.name || c.public_name || "Untitled"}</h3>
                  {c.published
                    ? <span className="text-[9px] uppercase tracking-[0.2em] rounded-full bg-[#6b7c3a]/12 text-[#4d5a24] px-2 py-0.5">Live</span>
                    : <span className="text-[9px] uppercase tracking-[0.2em] rounded-full bg-[#1e3d3a]/8 text-[#1e3d3a]/55 px-2 py-0.5">Draft</span>}
                  {c.anonymize && <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] rounded-full bg-[#a3823f]/12 text-[#a3823f] px-2 py-0.5"><ShieldOff className="h-2.5 w-2.5" /> Anon</span>}
                  {c.is_seed && <span className="text-[9px] uppercase tracking-[0.2em] rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/50 px-2 py-0.5">Placeholder</span>}
                </div>
                <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#1e3d3a]/50">{c.industry || "—"}</p>
                <p className="mt-2 text-sm text-[#1e3d3a]/70">
                  <span className="font-medium">{c.bbb_before || "—"} → {c.bbb_after || "—"}</span>
                  {" · "}${Number(c.savings_monthly || 0).toLocaleString()}/mo{" · "}{c.months || 0} mo
                  {c.plan ? ` · ${c.plan}` : ""}
                </p>
                {c.anonymize && (
                  <p className="mt-1 text-[11px] text-[#a3823f]">Public alias: {c.public_name || "Confidential client"}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button type="button" data-testid={`case-publish-${c.id}`} onClick={() => togglePublish(c)} title={c.published ? "Hide from site" : "Publish to site"}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                  {c.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                </button>
                <button type="button" data-testid={`case-edit-${c.id}`} onClick={() => startEdit(c)} title="Edit"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" data-testid={`case-delete-${c.id}`} onClick={() => setDelTarget(c)} title="Delete"
                  className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/40 hover:border-[#b23a48]/40 hover:bg-[#b23a48]/8 hover:text-[#b23a48] transition-colors duration-300">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div data-testid="case-editor-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !saving) setEditing(null); }}>
          <div className="w-full max-w-2xl rounded-2xl bg-[#eae7de] border border-[#c9a961]/40 p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] flex items-center gap-2"><Award className="h-3.5 w-3.5" /> {editing.id ? "Edit case study" : "New case study"}</p>
              <button type="button" onClick={() => setEditing(null)} className="text-[#1e3d3a]/45 hover:text-[#1e3d3a]"><X className="h-4 w-4" /></button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Client / business name *</label>
                <input data-testid="case-field-name" className={"mt-1 " + inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ridgeway Auto Repair" />
              </div>
              <div className="sm:col-span-2 rounded-xl border border-[#a3823f]/30 bg-[#c9a961]/8 p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="case-field-anonymize" checked={editing.anonymize} onChange={(e) => setEditing({ ...editing, anonymize: e.target.checked })} className="h-4 w-4 accent-[#a3823f]" />
                  <span className="text-sm text-[#1e3d3a] font-medium">Hide the real name &amp; sensitive info on the public site</span>
                </label>
                {editing.anonymize && (
                  <div className="mt-3">
                    <label className={labelCls}>Public alias (shown instead of the real name)</label>
                    <input data-testid="case-field-alias" className={"mt-1 " + inputCls} value={editing.public_name || ""} onChange={(e) => setEditing({ ...editing, public_name: e.target.value })} placeholder="Regional Auto Shop · Southwest" />
                    <p className="mt-1 text-[11px] text-[#1e3d3a]/55">Leave blank to show “Confidential client”.</p>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Industry · region</label>
                <input data-testid="case-field-industry" className={"mt-1 " + inputCls} value={editing.industry} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} placeholder="Auto Repair · Southwest" />
              </div>
              <div>
                <label className={labelCls}>BBB before</label>
                <input data-testid="case-field-before" className={"mt-1 " + inputCls} value={editing.bbb_before} onChange={(e) => setEditing({ ...editing, bbb_before: e.target.value })} placeholder="D" />
              </div>
              <div>
                <label className={labelCls}>BBB after</label>
                <input data-testid="case-field-after" className={"mt-1 " + inputCls} value={editing.bbb_after} onChange={(e) => setEditing({ ...editing, bbb_after: e.target.value })} placeholder="A-" />
              </div>
              <div>
                <label className={labelCls}>Savings / month ($)</label>
                <input data-testid="case-field-savings" type="number" className={"mt-1 " + inputCls} value={editing.savings_monthly} onChange={(e) => setEditing({ ...editing, savings_monthly: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Timeline (months)</label>
                <input data-testid="case-field-months" type="number" className={"mt-1 " + inputCls} value={editing.months} onChange={(e) => setEditing({ ...editing, months: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Plan</label>
                <select data-testid="case-field-plan" className={"mt-1 " + inputCls} value={editing.plan || ""} onChange={(e) => setEditing({ ...editing, plan: e.target.value })}>
                  {PLANS.map((p) => <option key={p} value={p}>{p || "— none —"}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Sort order</label>
                <input data-testid="case-field-sort" type="number" className={"mt-1 " + inputCls} value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Quote</label>
                <textarea data-testid="case-field-quote" rows={2} className={"mt-1 " + inputCls} value={editing.quote} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} placeholder="Payroll dropped 22% without losing a single line cook…" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Levers (comma separated)</label>
                <input data-testid="case-field-levers" className={"mt-1 " + inputCls}
                  value={Array.isArray(editing.levers) ? editing.levers.join(", ") : editing.levers}
                  onChange={(e) => setEditing({ ...editing, levers: e.target.value.split(",") })}
                  placeholder="Supplier renegotiation, Shift restructuring, Dispute strategy" />
              </div>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="case-field-published" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-[#6b7c3a]" />
                  <span className="text-sm text-[#1e3d3a]">Publish to the website now</span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button type="button" data-testid="case-save-btn" onClick={save} disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] px-3 disabled:opacity-60">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <div data-testid="case-delete-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDelTarget(null); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Case study · confirm</p>
            <h3 className="mt-1 font-head text-xl text-[#1e3d3a]">Delete this story?</h3>
            <p className="mt-2 text-sm text-[#1e3d3a]/70">“{delTarget.name || delTarget.public_name}” will be removed from the website and your library. This cannot be undone.</p>
            <div className="mt-6 flex items-center gap-3">
              <button type="button" data-testid="case-delete-confirm" onClick={confirmDelete}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#b23a48] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#8f2a37] transition-colors duration-300">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
              <button type="button" onClick={() => setDelTarget(null)} className="text-[11px] uppercase tracking-[0.22em] text-black/45 hover:text-[#1e3d3a] px-3">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Editable public headline stats ----
const METRIC_FIELDS = [
  { key: "clients", label: "Clients helped" },
  { key: "savings_monthly", label: "Monthly savings ($)" },
  { key: "grades_lifted", label: "BBB grades lifted" },
  { key: "avg_months", label: "Avg months to climb" },
];

function HeadlineStatsEditor() {
  const [auto, setAuto] = useState(null);       // computed metrics
  const [overrides, setOverrides] = useState({}); // manual overrides
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [{ data: m }, { data: s }] = await Promise.all([
        api.get("/case-studies/metrics"),
        api.get("/admin/site-settings"),
      ]);
      setAuto(m);
      setOverrides(s.success_metrics || {});
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    // Strip empty strings so blank = "use auto".
    const clean = {};
    Object.entries(overrides).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) clean[k] = v;
    });
    try {
      await api.put("/admin/site-settings", { success_metrics: clean });
      toast.success("Website stats saved.");
      await load();
    } catch { toast.error("Could not save stats."); }
    finally { setSaving(false); }
  };

  if (!auto) return null;
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="h-4 w-4 text-[#c9a961]" />
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Website headline stats</span>
      </div>
      <p className="text-sm text-[#1e3d3a]/70 max-w-2xl">
        These are the big numbers visitors see above your success wall. Leave a field blank to auto-calculate it
        from your published stories, or type a number to show exactly what you want.
      </p>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {METRIC_FIELDS.map((f) => (
          <div key={f.key}>
            <label className={labelCls}>{f.label}</label>
            <input
              data-testid={`stat-field-${f.key}`}
              type="number"
              className={"mt-1 " + inputCls}
              value={overrides[f.key] ?? ""}
              placeholder={`Auto: ${auto[f.key]}`}
              onChange={(e) => setOverrides({ ...overrides, [f.key]: e.target.value })}
            />
          </div>
        ))}
      </div>
      <div className="mt-4">
        <button type="button" data-testid="stats-save-btn" onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save stats
        </button>
      </div>
    </div>
  );
}
